import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FarmProductItem from "./FarmProductItem";
import FarmProcessedProductItem from "./FarmProcessedProductItem";

const previewLimit = () => {
  if (typeof window === "undefined") return 9;
  const compactLayout = window.matchMedia
    ? window.matchMedia("(max-width: 1200px)").matches
    : window.innerWidth <= 1200;
  return compactLayout ? 8 : 9;
};

export default function FarmProductsPreviewList({ products, kind, language }) {
  const [limit, setLimit] = useState(previewLimit);
  const [position, setPosition] = useState(0);
  const listRef = useRef(null);
  const visible = products.slice(0, limit);
  const isFresh = kind === "fresh";
  const Card = isFresh ? FarmProductItem : FarmProcessedProductItem;
  const title = isFresh
    ? (language === 0 ? "Färska produkter" : "Fresh products")
    : (language === 0 ? "Processade produkter" : "Processed products");

  useEffect(() => {
    const update = () => setLimit(previewLimit());
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    setPosition(0);
    if (listRef.current) listRef.current.scrollLeft = 0;
  }, [kind, limit, products]);

  const updatePosition = () => {
    const list = listRef.current;
    if (!list || !list.children.length) return;
    const first = list.children[0];
    const second = list.children[1];
    const step = second ? second.offsetLeft - first.offsetLeft : first.clientWidth;
    if (step > 0) setPosition(Math.min(visible.length - 1, Math.round(list.scrollLeft / step)));
  };

  const move = (direction) => {
    const list = listRef.current;
    const next = Math.max(0, Math.min(visible.length - 1, position + direction));
    const first = list?.children[0];
    const target = list?.children[next];
    if (first && target) list.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: "smooth" });
    setPosition(next);
  };

  return <div className="FarmProductsPreview">
    <div ref={listRef} className="FarmProductsGrid FarmProductsPreviewGrid" onScroll={updatePosition} role="region" aria-label={title}>
      {visible.map((item) => <Card key={item.firebaseId} item={item} />)}
    </div>
    <div className={`FarmProductsPreviewFooter${products.length > visible.length ? " hasMore" : ""}`}>
      {products.length > visible.length && <Link className="FarmProductsViewAll" to={`/farm/products/${kind}`} aria-label={language === 0 ? `Visa alla ${title.toLocaleLowerCase("sv")}` : `View all ${title.toLowerCase()}`}>
        {language === 0 ? "Visa alla" : "View all"}
        <ArrowForwardIcon aria-hidden="true" />
      </Link>}
      <div className="FarmProductsCarouselControls" role="group" aria-label={language === 0 ? `Bläddra bland ${title.toLocaleLowerCase("sv")}` : `Browse ${title.toLowerCase()}`}>
        <span className="FarmProductsCarouselCount" aria-live="off">{String(position + 1).padStart(2, "0")} / {String(visible.length).padStart(2, "0")}</span>
        <button type="button" onClick={() => move(-1)} disabled={position === 0} aria-label={language === 0 ? "Föregående produkt" : "Previous product"}><ArrowBackIcon /></button>
        <button type="button" onClick={() => move(1)} disabled={position >= visible.length - 1} aria-label={language === 0 ? "Nästa produkt" : "Next product"}><ArrowForwardIcon /></button>
      </div>
    </div>
  </div>;
}
